import { getUploadResult } from './uploader';
import type { IEncodeResult, IPayloadData } from './uploader';

type IUploadQueueOptions = IEncodeResult & {
    status: EUploadStatus;
    progress: {
        percent: number;
    };
};

interface IUploadTask {
    options: IUploadQueueOptions;
    resolve: (value: IPayloadData | PromiseLike<IPayloadData>) => void;
    reject: (reason?: any) => void;
}

enum EUploadStatus {
    uploading,
    success,
    error,
}

class UploadQueue {
    private queue: IUploadTask[] = [];
    private activeCount = 0;
    private readonly maxConcurrentUploads: number;
    private onAllComplete = (_payloadDataList: IPayloadData[]) => {};
    private payloadDataList: IPayloadData[] = [];

    constructor(maxConcurrentUploads: number, options?: { onAllComplete?: (payloadDataList: IPayloadData[]) => void }) {
        this.maxConcurrentUploads = maxConcurrentUploads;
        const { onAllComplete } = options || {};
        if (onAllComplete) {
            this.onAllComplete = onAllComplete;
        }
    }

    add(options: IUploadQueueOptions): Promise<IPayloadData> {
        return new Promise((resolve, reject) => {
            this.queue.push({ options, resolve, reject });
            this.processQueue();
        });
    }

    private processQueue() {
        if (this.activeCount >= this.maxConcurrentUploads || this.queue.length === 0) {
            if (this.activeCount === 0 && this.queue.length === 0) {
                this.onAllComplete(this.payloadDataList);
                this.payloadDataList = [];
            }
            return;
        }

        const task = this.queue.shift();
        if (task) {
            this.activeCount++;
            getUploadResult(task.options)
                .then((result) => {
                    task.resolve(result);
                    this.activeCount--;
                    this.payloadDataList.push(result);
                    this.processQueue();
                })
                .catch((error) => {
                    task.reject(error);
                    this.activeCount--;
                    this.processQueue();
                });
        }
    }

    get length() {
        return this.queue.length;
    }
}

export default UploadQueue;
export { EUploadStatus };
export type { IUploadQueueOptions, IUploadTask, IEncodeResult, IPayloadData };
